/*
 * The constructed still (Master Design System §12): what the ground shows on
 * an index and while a live map is still arriving. A blurred survey of the
 * §09 map — harbour to the right, parks to the left, the street grid and one
 * warm arterial — drawn in the `map` token group and blurred INSIDE the
 * asset, so nothing is ever a runtime filter over the live basemap (D5). It
 * is one SVG the browser rasterises once; the engine only fades the layer
 * that holds it.
 *
 * The composition follows the canvas's ground-blur.png (1500×1700): water
 * top-right and along the right edge, the harbour as a diagonal band, a bay
 * lower-right, green along the left and in patches at the top, a grid of
 * paper roads at a slight angle, and stone calm where the leaf lands. The
 * layer is landscape and anchored to the top of the picture, so the band
 * above the sheet shows the harbour's edge and the parks, as the canvas does.
 */

const W = 1500
const H = 1700
const BLUR = 14

/** The street grid: paper lines about 100 units apart, never quite even, turned a few degrees like a real suburb. */
const STEPS = [92, 108, 96, 118, 88, 104]
const run = (from: number, count: number) => {
  const out: number[] = []
  let at = from
  for (let i = 0; i < count; i++) {
    out.push(at)
    at += STEPS[i % STEPS.length]
  }
  return out
}
const COLUMNS = run(60, 16)
const ROWS = run(40, 18)

export function GroundStill() {
  return (
    <svg
      aria-hidden="true"
      className="block h-full w-full"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMin slice"
    >
      <defs>
        <filter
          id="ground-still-blur"
          x="-5%"
          y="-5%"
          width="110%"
          height="110%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation={BLUR} />
        </filter>
        {/* the grid is a survey of the top of the picture; it dissolves where the leaf lands */}
        <linearGradient id="ground-still-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--color-map-road-core)" />
          <stop
            offset="0.45"
            stopColor="var(--color-map-road-core)"
            stopOpacity="0.7"
          />
          <stop
            offset="0.85"
            stopColor="var(--color-map-road-core)"
            stopOpacity="0"
          />
        </linearGradient>
        <mask id="ground-still-grid-mask">
          <rect width={W} height={H} fill="url(#ground-still-fade)" />
        </mask>
      </defs>

      {/* the ground itself is not blurred, so the edges never thin out */}
      <rect className="fill-map-ground" width={W} height={H} />

      <g filter="url(#ground-still-blur)">
        {/* land: paper over stone, so the blocks read pale */}
        <rect
          className="fill-map-road-core"
          width={W}
          height={H}
          opacity={0.55}
        />
        {/* the warm middle: say.200 behind the built-up land */}
        <path
          className="fill-map-road-major"
          opacity={0.2}
          d="M380 330 C560 300 760 360 900 420 C1060 490 1160 620 1120 800 C1080 960 900 1000 740 980 C560 960 420 860 360 700 C300 560 280 400 380 330 Z"
        />
        <path
          className="fill-map-road-major"
          opacity={0.18}
          d="M520 1080 C700 1040 900 1120 1000 1240 C1080 1360 980 1520 820 1560 C640 1600 520 1500 480 1360 C450 1240 440 1120 520 1080 Z"
        />

        {/* the lilac of a residential grid, top-left */}
        <path
          className="fill-map-transit"
          opacity={0.32}
          d="M0 110 C100 90 250 120 330 170 C380 230 340 300 250 320 C150 330 60 300 0 320 Z"
        />

        {/* water: the open water top-right and down the right edge */}
        <path
          className="fill-map-water"
          d="M960 0 H1500 V640 L1420 580 C1380 520 1300 500 1200 480 C1130 460 1110 400 1080 360 C1050 320 990 320 930 300 C900 240 940 120 960 0 Z"
        />
        <path
          className="fill-map-water"
          opacity={0.85}
          d="M1500 600 V1000 L1380 980 C1330 900 1360 780 1420 700 Z"
        />
        {/* the harbour: a band across the lower half */}
        <path
          className="fill-map-water"
          opacity={0.8}
          d="M420 1300 C600 1220 800 1150 1000 1020 C1150 930 1300 880 1500 850 V960 C1300 980 1150 1040 1020 1110 C820 1210 640 1290 470 1370 Z"
        />
        {/* the bay lower-right, and the glow beyond it */}
        <path
          className="fill-map-water"
          opacity={0.6}
          d="M1050 1180 C1150 1120 1350 1150 1450 1250 C1500 1330 1450 1450 1300 1480 C1150 1500 1020 1400 1030 1290 Z"
        />
        <rect
          className="fill-map-water"
          opacity={0.35}
          x={980}
          y={1340}
          width={520}
          height={360}
        />
        {/* a lavender wash centre-left where a reserve meets the water */}
        <ellipse
          className="fill-map-water"
          opacity={0.3}
          cx={430}
          cy={850}
          rx={200}
          ry={150}
        />

        {/* parks: the long green down the left, and patches along the top */}
        <path
          className="fill-map-landcover"
          d="M0 290 C120 280 260 320 340 400 C380 520 330 640 260 760 C200 880 220 1050 140 1200 C80 1350 40 1500 0 1700 Z"
        />
        <path
          className="fill-map-landcover"
          opacity={0.55}
          d="M0 800 C150 820 300 900 380 1050 C450 1200 380 1400 300 1520 C220 1640 100 1700 0 1700 Z"
        />
        <path
          className="fill-map-landcover"
          d="M300 60 C420 40 560 80 620 140 C660 210 640 280 560 330 C480 360 400 340 340 280 C290 220 280 120 300 60 Z"
        />
        <path
          className="fill-map-landcover"
          d="M640 0 H900 C910 60 880 110 820 130 C740 150 660 110 640 60 Z"
        />
        <path
          className="fill-map-wood"
          opacity={0.7}
          d="M850 230 C920 200 1010 220 1080 280 C1130 330 1100 390 1020 400 C940 410 860 380 840 320 Z"
        />

        {/* the street grid, paper on everything, a few degrees off true */}
        <g
          className="stroke-map-road-core"
          fill="none"
          strokeWidth={10}
          opacity={0.8}
          mask="url(#ground-still-grid-mask)"
          transform={`rotate(-4 ${W / 2} ${H / 2})`}
        >
          {COLUMNS.map((x) => (
            <line key={`c${x}`} x1={x} y1={-100} x2={x} y2={H + 100} />
          ))}
          {ROWS.map((y) => (
            <line key={`r${y}`} x1={-100} y1={y} x2={W + 100} y2={y} />
          ))}
        </g>
        {/* one warm arterial, north to the harbour */}
        <path
          className="stroke-map-road-major"
          fill="none"
          strokeWidth={14}
          opacity={0.9}
          d="M545 0 V640"
        />
      </g>
    </svg>
  )
}
